import { WithServiceId } from '@mews-commander/types';
import { IconName, Stack } from '@optimus-web/core';
import { FormInputField, LocalizedText } from '@mews/framework';
import { TextKey, SearchType } from '@mews-commander/enums';
import { AvailabilityBlock, ReservationPurpose, ServiceOrderOrigin } from '@mews-commander/data-access';
import { TooltippedIcon } from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import { EnumSelectField } from '@mews-commander/form';
import {
    isCurrentProfileEnabledToManageCustomers,
    isCurrentProfileEnabledToManageCompanies,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector } from '@mews-commander/utils';
import { SearchHookField } from '../../../../../components/form/hookFields/SearchHookField';

export type ClientInfoFormValues = Pick<
    AvailabilityBlock,
    'bookerId' | 'companyId' | 'travelAgencyId' | 'reservationPurpose' | 'origin' | 'externalIdentifier'
>;

export const ClientInfoFields = ({ serviceId }: WithServiceId) => {
    const canCreateCustomers: boolean = useCommanderSelector(isCurrentProfileEnabledToManageCustomers);
    const canCreateCompanies: boolean = useCommanderSelector(isCurrentProfileEnabledToManageCompanies);

    return (
        <Stack vertical spacing="300">
            <SearchHookField
                name="bookerId"
                label={<LocalizedText textKey={TextKey.Booker} />}
                searchTypes={[SearchType.Customer]}
                newEntity={canCreateCustomers}
                required
            />
            <SearchHookField
                name="companyId"
                label={<LocalizedText textKey={TextKey.Company} />}
                searchTypes={[SearchType.Company]}
                newEntity={canCreateCompanies}
            />
            <SearchHookField
                name="travelAgencyId"
                label={<LocalizedText textKey={TextKey.TravelAgency} />}
                searchTypes={[SearchType.TravelAgency]}
                optionalSearchParams={{ serviceIds: [serviceId] }}
            />
            <EnumSelectField
                label={<LocalizedText textKey={TextKey.ReservationPurpose} />}
                enumClass={ReservationPurpose}
                name="reservationPurpose"
                enumPrefix="ReservationPurpose"
                showEmptyValue
            />
            <EnumSelectField
                label={<LocalizedText textKey={TextKey.Origin} />}
                enumClass={ServiceOrderOrigin}
                name="origin"
                enumPrefix="ServiceOrderOrigin"
                disabled
            />
            <FormInputField
                name="externalIdentifier"
                label={<LocalizedText textKey={TextKey.ExternalIdentifier} />}
                captionIconWithTooltip={
                    <TooltippedIcon
                        size="small"
                        value={IconName.Info}
                        tooltip={<LocalizedText textKey={TextKey.AvailabilityBlockExternalIdentifierTooltip} />}
                    />
                }
                placeholder={UnicodeEntity.Dash}
                disabled
            />
        </Stack>
    );
};
